// ─────────────────────────────────────────────────────────────────────────────
// KnowledgeBase Models
// ─────────────────────────────────────────────────────────────────────────────
// Domain models for the Knowledge Base feature.
// Covers: file documents, filter types, and upload payloads.
// ─────────────────────────────────────────────────────────────────────────────

/** Status of a knowledge-base document after indexing. */
export const KbDocumentStatus = {
  INDEXED: 'indexed',
  PROCESSING: 'processing',
  UNKNOWN: 'unknown',
} as const;
export type KbDocumentStatus = (typeof KbDocumentStatus)[keyof typeof KbDocumentStatus];

/** Allowed API document types. */
export const KbDocumentType = {
  GENERAL: 'general',
  PRODUCT_CATALOG: 'product_catalog',
  PRICE_LIST: 'price_list',
  CUSTOMER_PROFILE: 'customer_profile',
  CONTRACT: 'contract',
  SHIPPING_POLICY: 'shipping_policy',
  RETURN_POLICY: 'return_policy',
  TECHNICAL_SPECIFICATIONS: 'technical_specifications',
  MARKETING_MATERIAL: 'marketing_material',
  ORDER_HISTORY: 'order_history',
  INVENTORY: 'inventory',
} as const;
export type KbDocumentType = (typeof KbDocumentType)[keyof typeof KbDocumentType];

export const KbScopeLevel = {
  GENERAL: 'general',
  PRODUCT: 'product',
  CUSTOMER: 'customer',
} as const;
export type KbScopeLevel = (typeof KbScopeLevel)[keyof typeof KbScopeLevel];

export interface IKbApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
}

export interface IKbUploadDocumentMetadataDto {
  document_type?: KbDocumentType;
  document_types?: KbDocumentType[];
  tags?: string[];
  brands?: string[];
  language?: string | null;
  is_confidential?: boolean;
  customer_ids?: string[];
  customer_tags?: string[];
  product_skus?: string[];
  product_categories?: string[];
  version?: number;
  user_comment?: string | null;
}

export interface IKbDocumentMetadataDto extends IKbUploadDocumentMetadataDto {
  environment_id?: string;
  filename?: string;
  file_size?: number;
  upload_date?: string;
  updated_at?: string;
  scope_level?: KbScopeLevel;
  uploaded_by?: string;
  updated_by?: string;
  customer_scope?: {
    scope: string;
    company_ids?: number[] | null;
    group_ids?: number[] | null;
  };
  product_scope?: {
    scope: string;
    brand_ids?: number[];
    category_ids?: number[];
    label_ids?: number[];
    product_ids?: number[];
  };
}

export interface IKbDocumentIndexStatusDto {
  status: KbDocumentStatus;
  index_time?: string;
}

export interface IKbApiDocumentDto {
  document_id: string;
  name: string;
  gcs_uri: string;
  mime_type?: string;
  metadata?: IKbDocumentMetadataDto;
  index_status?: IKbDocumentIndexStatusDto;
}

export interface IKbListDocumentsDataDto {
  documents: IKbApiDocumentDto[];
  total: number;
  page_token?: string;
}

export interface IKbUploadResultDto {
  document?: IKbApiDocumentDto;
  filename: string;
  status: 'created' | 'error';
  error?: string;
}

export interface IKbUploadResponseDataDto {
  created: number;
  errors: number;
  results: IKbUploadResultDto[];
}

export interface IKbDeleteDocumentDataDto {
  document_ids?: string[];
  deleted?: boolean;
}

export interface IKbDownloadResultDto {
  document_id: string;
  download_url: string;
  filename: string;
  mime_type: string;
}

export interface IKbDownloadDocumentsDataDto {
  results: IKbDownloadResultDto[];
}

/** Flattened UI model derived from the API DTOs. */
export interface IKbDocument {
  id: string;
  fileName: string;
  /** Primary type (= types[0]); drives the table column, type filter and sorting. */
  type: KbDocumentType;
  /** Full set of classifications. Falls back to [type] until the ATO API round-trips the array. */
  types: KbDocumentType[];
  status: KbDocumentStatus;
  uploadDate: string | null;
  updatedAt: string | null;
  aiOverview: string | null;
  userComment: string | null;
  tags: string[];
  brands: string[];
  scopeLevel: KbScopeLevel;
  language: string | null;
  isConfidential: boolean;
  mimeType: string | null;
  gcsUri: string;
  uploadedBy: string | null;
  updatedBy: string | null;
  indexTime: string | null;
  customerScope: IKbCustomerScope | null;
  productScope: IKbProductScope | null;
}

/** Summary stats derived from the loaded document list. */
export interface IKbStats {
  indexedCount: number;
  lastUploadDate: string | null;
}

/** Filter tab definition for the type filter bar. */
export interface IKbTypeFilter {
  type: KbDocumentType | 'all';
  labelKey: string;
  count: number;
}

export interface IKbCustomerScope {
  scope: 'all' | 'by_company' | 'by_group';
  companyIds?: number[];
  groupIds?: number[];
}

export interface IKbProductScope {
  scope: 'all' | 'by_brand_category_label' | 'manual';
  brandIds: number[];
  categoryIds: number[];
  labelIds: number[];
  productIds: number[];
}

/** Metadata shape sent to the API (shared by upload & update). */
export interface IKbRequestMetadata {
  document_type?: KbDocumentType;
  document_types?: KbDocumentType[];
  tags?: string[];
  user_comment?: string;
  customer_scope?: {
    scope: 'all' | 'by_company' | 'by_group';
    company_ids?: number[] | null;
    group_ids?: number[] | null;
  };
  product_scope?: {
    scope: 'all' | 'by_brand_category_label' | 'manual';
    brand_ids?: number[] | null;
    category_ids?: number[] | null;
    label_ids?: number[] | null;
    product_ids?: number[] | null;
  };
}

/** Payload for creating a new document (file required). */
export interface IKbUploadRequest {
  file: File;
  metadata: IKbRequestMetadata;
}

/** Payload for updating an existing document (file optional). */
export interface IKbUpdateRequest {
  documentId: string;
  file?: File;
  metadata: IKbRequestMetadata;
}
